/**
 * smooth-scroll.ts - Custom eased smooth-scroll helper (Wave 0 foundation).
 *
 * Promoted verbatim from the inline `smoothScrollTo` in `LandingHero.tsx`
 * so the homepage redesign can reuse it (chevron jump, anchor links into
 * the loop/CTA). Ease-in-out cubic over `duration` ms so the handoff feels
 * deliberate rather than instant.
 *
 * Walks up from the target element to find an `overflow-y: auto|scroll`
 * ancestor (the real scroll container); falls back to `documentElement`
 * (which is the actual scroller on the landing page). Offsets the final
 * scroll position by `navHeight` so the target lands below the fixed nav.
 */
export function smoothScrollTo(
  el: HTMLElement,
  duration = 1200,
  navHeight = 64,
) {
  let container: Element | null = el.parentElement;
  while (container && container !== document.documentElement) {
    const ov = getComputedStyle(container).overflowY;
    if (ov === "auto" || ov === "scroll") break;
    container = container.parentElement;
  }
  if (!container) container = document.documentElement;

  const start = container.scrollTop;
  const target = el.getBoundingClientRect().top + start - navHeight;
  const distance = target - start;
  let startTime: number | null = null;

  function step(time: number) {
    if (!startTime) startTime = time;
    const t = Math.min((time - startTime) / duration, 1);
    const ease = t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;
    container!.scrollTop = start + distance * ease;
    if (t < 1) requestAnimationFrame(step);
  }
  requestAnimationFrame(step);
}
